import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getActiveUsers } from "../api/activeUserService";

// Ключи таблиц: у каждой таблицы свои фильтры и свои данные
export const TABLE_KEYS = {
  activeUsers: "activeUsers",
  segmentation: "segmentation",
};

export const DEFAULT_FILTERS = {
  period: "",
  registerDateFrom: "",
  registerDateTo: "",
  isSubscribed: false,
  subscriptionActivatedFrom: "",
  subscriptionActivatedTo: "",
  subscriptionType: "",
  activityLevel: "",
  search: "",
  segmentCodes: [],
};

export const DEFAULT_QUERY = {
  filters: DEFAULT_FILTERS,
  page: 1,
  pageLimit: 10,
  dynamicDate: "",
};

// Стабильный ключ запроса — по нему понимаем, соответствуют ли данные в сторе текущим фильтрам
export function getQueryKey(query) {
  const { filters = {}, page, pageLimit, dynamicDate } = query;
  return JSON.stringify({
    ...DEFAULT_FILTERS,
    ...filters,
    segmentCodes: filters.segmentCodes || [],
    page,
    pageLimit,
    dynamicDate,
  });
}

const createTableState = () => ({
  initialized: false, // query ещё не проинициализирован (из URL) после загрузки страницы
  query: DEFAULT_QUERY,
  data: [],
  total: 0,
  loading: false,
  error: null,
  fetchedKey: null, // ключ запроса, для которого загружены (или грузятся) data
  requestId: null,
  sortOrder: "default",
});

const initialState = {
  [TABLE_KEYS.activeUsers]: createTableState(),
  [TABLE_KEYS.segmentation]: createTableState(),
};

export const fetchActiveUsers = createAsyncThunk(
  "activeUsers/fetch",
  async ({ query }, { rejectWithValue }) => {
    try {
      return await getActiveUsers(query);
    } catch (e) {
      return rejectWithValue(
        e?.message || "Ошибка при получении пользователей",
      );
    }
  },
);

const activeUsersSlice = createSlice({
  name: "activeUsers",
  initialState,
  reducers: {
    setQuery(state, action) {
      const { tableKey, query } = action.payload;
      state[tableKey].query = query;
      state[tableKey].initialized = true;
    },
    setSortOrder(state, action) {
      const { tableKey, sortOrder } = action.payload;
      state[tableKey].sortOrder = sortOrder;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActiveUsers.pending, (state, action) => {
        const { tableKey, query } = action.meta.arg;
        const table = state[tableKey];
        table.loading = true;
        table.error = null;
        table.fetchedKey = getQueryKey(query);
        table.requestId = action.meta.requestId;
      })
      .addCase(fetchActiveUsers.fulfilled, (state, action) => {
        const table = state[action.meta.arg.tableKey];
        // Игнорируем ответы устаревших запросов
        if (table.requestId !== action.meta.requestId) return;
        table.data = action.payload.items;
        table.total = action.payload.total;
        table.loading = false;
      })
      .addCase(fetchActiveUsers.rejected, (state, action) => {
        const table = state[action.meta.arg.tableKey];
        if (table.requestId !== action.meta.requestId) return;
        table.error = action.payload || action.error.message;
        table.loading = false;
      });
  },
});

export const { setQuery, setSortOrder } = activeUsersSlice.actions;

export const selectTableState = (tableKey) => (state) =>
  state.activeUsers[tableKey];

export default activeUsersSlice.reducer;
